import { useState } from "react";

import { useAddWard } from "../../../hooks/mutations/admin/wards";
import { useHospitalOptions } from "../../../hooks/queries/admin/selectValues";
import { useToast } from "../../../hooks/useToast";
import { errorToastMessage } from "../../../utils/HttpError";
import {
  AdminSelectField,
  CheckboxField,
  FieldError,
  FilterButton,
  FilterGroup,
  FilterInput,
  FilterLabel,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
} from "../shared";

interface IAddWardModalProps {
  onClose: () => void;
}

/** The API caps both at 70 characters (AddWardRequestDto). */
const MAX_TEXT_LENGTH = 70;

/**
 * Create-ward modal — Retool's `formWard` in create mode (EN-5151). Retool
 * used one modal for both create and edit; the portal splits them because the
 * edit view embeds the audit trail, which needs the global modal slot for its
 * own drill-down.
 */
export function AddWardModal({ onClose }: IAddWardModalProps) {
  const { showToast } = useToast();
  const { hospitals, isLoading: hospitalsLoading } = useHospitalOptions();
  const { addWard, isAddingWard } = useAddWard();

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [groupId, setGroupId] = useState("");
  // Retool's create form defaulted Publish on, matching the API's own default.
  const [publish, setPublish] = useState(true);
  // An empty create form is invalid by definition; showing every error before
  // the operator has typed anything is just noise. A field only complains
  // once it has been touched — the submit button carries the "not yet valid"
  // signal in the meantime.
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const touch = (field: string) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const errors = {
    code: code.trim() ? undefined : "Code is required.",
    name: name.trim() ? undefined : "Name is required.",
    groupId: groupId ? undefined : "Select a hospital.",
  };
  const hasErrors = Object.values(errors).some(Boolean);
  const shownError = (field: keyof typeof errors) =>
    touched[field] ? errors[field] : undefined;

  const handleAdd = async () => {
    if (hasErrors) return;

    try {
      await addWard({
        code: code.trim(),
        name: name.trim(),
        groupId: Number(groupId),
        publish,
      });
      showToast("The ward was created successfully.", "success");
      onClose();
    } catch (error) {
      showToast(
        errorToastMessage(error, "There was an error creating this ward."),
        "warning"
      );
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>Create ward</ModalTitle>

      <ModalForm>
        <FilterGroup>
          <FilterLabel htmlFor="new-ward-code">Code</FilterLabel>
          <FilterInput
            id="new-ward-code"
            type="text"
            maxLength={MAX_TEXT_LENGTH}
            value={code}
            onBlur={() => touch("code")}
            onChange={(e) => {
              touch("code");
              setCode(e.target.value);
            }}
          />
          {shownError("code") && <FieldError>{shownError("code")}</FieldError>}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="new-ward-name">Name</FilterLabel>
          <FilterInput
            id="new-ward-name"
            type="text"
            maxLength={MAX_TEXT_LENGTH}
            value={name}
            onBlur={() => touch("name")}
            onChange={(e) => {
              touch("name");
              setName(e.target.value);
            }}
          />
          {shownError("name") && <FieldError>{shownError("name")}</FieldError>}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="new-ward-hospital">Hospital</FilterLabel>
          <AdminSelectField
            id="new-ward-hospital"
            value={groupId}
            disabled={hospitalsLoading}
            onBlur={() => touch("groupId")}
            onChange={(e) => {
              touch("groupId");
              setGroupId(e.target.value);
            }}
          >
            <option value="">
              {hospitalsLoading ? "Loading..." : "Select an option"}
            </option>
            {hospitals.map((hospital) => (
              <option key={hospital.id} value={hospital.id}>
                {hospital.name}
              </option>
            ))}
          </AdminSelectField>
          {shownError("groupId") && (
            <FieldError>{shownError("groupId")}</FieldError>
          )}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Visibility</FilterLabel>
          <CheckboxField>
            <input
              type="checkbox"
              checked={publish}
              onChange={(e) => setPublish(e.target.checked)}
            />
            Published
          </CheckboxField>
        </FilterGroup>

        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isAddingWard}
            onClick={onClose}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isAddingWard || hasErrors}
            onClick={handleAdd}
          >
            {isAddingWard ? "Creating..." : "Create"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
